// intercessions.js
// Pure helpers for the family evening intercessions — no React, no network.
// Spec: docs/specs/SPIRIT_Intercessions_Spec_v2.md (§2.1 render rules, §2.2
// bands). Rows come in as Prayer_Intentions rows the way figApi flattens
// them ({ id, Title, Kind, Guide?, SortOrder, Active? }); absence rules:
//   - Active absent or true → shown; only an explicit false hides
//   - Guide absent → no guide; '' is treated the same but never written
//   - SortOrder absent → sorts last (Infinity), never NaN

export const KINDS = ['Intention', 'Leader', 'All', 'Pause'];
export const TITLE_MAX = 255;                       // SharePoint single-line Title cap
export const RESPONSE = 'Lord, hear our prayer.';
export const PETITION_SUFFIX = ', we pray to the Lord.';

// §2.2 — the Add picker's bands. `start` is where a new intention lands
// when the band is empty; `end` bounds the band for nextSortOrder.
export const BANDS = [
  { id: 'church',    label: 'Holy Father / Church', guide: 'Holy Father',  start: 100, end: 199 },
  { id: 'parish',    label: 'Parish',               guide: 'Our parish',   start: 200, end: 299 },
  { id: 'leaders',   label: 'Leaders / World',      guide: 'Our leaders',  start: 300, end: 399 },
  { id: 'sick',      label: 'Sick / Suffering',     guide: 'The sick',     start: 400, end: 499 },
  { id: 'departed',  label: 'Departed',             guide: 'Our departed', start: 500, end: 599 },
  { id: 'family',    label: 'Family / Friends',     guide: 'Family',       start: 600, end: 699 },
  { id: 'other',     label: 'Other',                guide: 'Other',        start: 700, end: 799 },
];

export const isActive = (row) => row?.Active !== false;

export const sortValue = (row) => {
  const n = Number(row?.SortOrder);
  return row?.SortOrder === undefined || row?.SortOrder === null || row?.SortOrder === '' || Number.isNaN(n) ? Infinity : n;
};

/** Rows in SortOrder, ties broken by numeric id so the order is stable. */
export function sortRows(rows) {
  return [...(rows || [])].sort((a, b) => (sortValue(a) - sortValue(b)) || (Number(a.id) - Number(b.id)));
}

/** Guide text as shown — trimmed, '' when absent. */
export const guideOf = (row) => String(row?.Guide ?? '').trim();

/** Trailing punctuation off an Intention title before the suffix goes on. */
export const stripTrailingPunctuation = (s) => String(s ?? '').trim().replace(/[.,;:!?…]+$/u, '');

/**
 * §2.1 — one row → what pray mode prints. Every entry carries the source
 * row and a `lines` list of { role, text } where role is 'guide' | 'leader'
 * | 'all' | 'pause'. Inactive rows are dropped unless includeInactive.
 */
export function renderModel(rows, { includeInactive = false } = {}) {
  const ordered = sortRows(rows).filter((r) => includeInactive || isActive(r));
  return ordered.map((row) => {
    const kind = KINDS.includes(row.Kind) ? row.Kind : 'Leader';
    const title = String(row.Title ?? '').trim();
    const guide = guideOf(row);
    const lines = [];
    if (guide) lines.push({ role: 'guide', text: guide });
    if (kind === 'Intention') {
      lines.push({ role: 'leader', text: `${stripTrailingPunctuation(title)}${PETITION_SUFFIX}` });
      lines.push({ role: 'all', text: RESPONSE });
    } else if (kind === 'All') {
      lines.push({ role: 'all', text: title });
    } else if (kind === 'Pause') {
      lines.push({ role: 'pause', text: title });
    } else {
      lines.push({ role: 'leader', text: title });
    }
    return { id: row.id, kind, active: isActive(row), row, lines };
  });
}

/** Band lookup by id; null for an unknown id. */
export const bandById = (id) => BANDS.find((b) => b.id === id) || null;

/**
 * §2.2 — the SortOrder a new intention takes in a band: max in the band + 10,
 * or the band start if the band is empty. Rows outside the band never count,
 * and an inactive row still holds its slot.
 */
export function nextSortOrder(band, rows) {
  const b = typeof band === 'string' ? bandById(band) : band;
  if (!b) throw new Error(`unknown band: ${band}`);
  const inBand = (rows || []).map(sortValue).filter((n) => Number.isFinite(n) && n >= b.start && n <= b.end);
  return inBand.length ? Math.max(...inBand) + 10 : b.start;
}

/**
 * "Add line" — a liturgy line placed SortOrder + 5 after a chosen row. Pure
 * arithmetic; the caller decides what to do on a collision.
 */
export const sortAfter = (row) => {
  const n = sortValue(row);
  return Number.isFinite(n) ? n + 5 : 0;
};

/**
 * Up / down arrows swap SortOrder between neighbours. Returns the two
 * writes as [{ id, SortOrder }, { id, SortOrder }]; if the pair shares a
 * SortOrder (a collision from a manual edit), the second gets +1 so the
 * swap is visible.
 */
export function swapOrder(a, b) {
  const sa = sortValue(a);
  const sb = sortValue(b);
  const na = Number.isFinite(sb) ? sb : 0;
  let nb = Number.isFinite(sa) ? sa : 0;
  if (na === nb) nb = na + 1;
  return [{ id: a.id, SortOrder: na }, { id: b.id, SortOrder: nb }];
}

/** Title within the SharePoint cap? Counts code points, as SharePoint does. */
export const titleTooLong = (s) => [...String(s ?? '')].length > TITLE_MAX;
export const titleLength = (s) => [...String(s ?? '')].length;

/** Count of "Lord, hear our prayer." responses in a render model. */
export const responseCount = (model) => model.reduce((n, e) => n + e.lines.filter((l) => l.role === 'all' && l.text === RESPONSE).length, 0);
